import * as THREE from 'three'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js'
import { RectAreaLightUniformsLib } from 'three/addons/lights/RectAreaLightUniformsLib.js'

globalThis.LyModelViewerBase = class extends HTMLElement {
    #resizeObserver = null
    /** 渲染器尺寸 */
    get #rendererSize() {
        const rect = this.getBoundingClientRect()
        return [rect.width, rect.height]
    }
    /** 模型loader */
    #gltfLoader = new GLTFLoader()

    /** 渲染器 */
    #renderer = null
    /** 场景 */
    #scene = null
    /** 模型root组 */
    _rootGroup = null
    /** 相机 */
    #camera = null
    /** 相机初始位置 */
    #cameraInitPos = [0, 0, 30]
    /** 轨道控制器 */
    _orbit = null

    /** 是否正在渲染 */
    _isRendering = false
    /** 是否需要渲染一次 */
    _needRenderOnce = true
    /** 渲染请求队列 */
    #renderRequests = new Set()
    /** 是否需要渲染 */
    get #needRenderFrame() {
        return this._needRenderOnce || this.#renderRequests.size > 0
    }

    _requestRender = (key) => {
        if (this.#renderRequests.has(key)) return
        this.#renderRequests.add(key)
    }

    _cancelRender = (key) => {
        this.#renderRequests.delete(key)
    }

    #animationMixers = []
    #clock = new THREE.Clock()

    constructor() {
        super()

        const dracoLoader = new DRACOLoader()
        // 使用官方 CDN 解码器
        dracoLoader.setDecoderPath('https://cdn.jsdelivr.net/npm/three@0.174.0/examples/jsm/libs/draco/')
        // 绑定到 #gltfLoader
        this.#gltfLoader.setDRACOLoader(dracoLoader)
    }

    connectedCallback() {
        this.#resizeObserver = new ResizeObserver(this._resizeHandler)
        this.#resizeObserver.observe(this)
    }

    disconnectedCallback() {
        this._stopRenderLoop()

        this.#resizeObserver.disconnect()
        this.#resizeObserver = null

        this._orbit?.dispose()
        this._orbit?.removeEventListener('change', this._registerOrbitRenderRequest)
        this.#renderer?.dispose()
        this.#renderer?.domElement.remove()

        this.#scene?.traverse(obj => this._disposeObject3D(obj))
        this.#scene?.clear()

        this.#renderer = null
        this.#scene = null
        this.#camera = null
        this._rootGroup = null
        this._orbit = null

        this.#animationMixers.length = 0

        THREE.Cache.clear()
    }

    /** 初始化入口 */
    async _initBase() {
        this._initRenderer()
        this._initScene()
        this._initMainGroup()
        this._initCamera()
        this._initLight()

        this._initOrbitController()
        this._startRenderLoop()
    }

    /** 初始化渲染器 */
    _initRenderer = () => {
        // 创建一个 WebGL 渲染器，并开启抗锯齿，让边缘更平滑
        const renderer = new THREE.WebGLRenderer({ antialias: true })

        // 设置渲染画布的宽高（通常与容器或窗口大小一致）
        renderer.setSize(...this.#rendererSize)

        // 设置像素比，使高分辨率屏幕（如 Retina）下画面更清晰
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

        // 设置渲染输出的颜色空间为 sRGB，使颜色显示更准确、更接近人眼感知
        renderer.outputColorSpace = THREE.SRGBColorSpace

        // 使用 ACES Filmic 色调映射，使高光和暗部过渡更自然，整体观感更电影化
        renderer.toneMapping = THREE.ACESFilmicToneMapping

        // 设置曝光度，避免画面过暗或过亮
        renderer.toneMappingExposure = 1

        // 启用物理正确的光照计算，使光照衰减和反射更符合真实物理规律
        renderer.physicallyCorrectLights = true

        this.appendChild(renderer.domElement)
        this.#renderer = renderer
    }

    /** 初始化场景 */
    _initScene = () => {
        const scene = new THREE.Scene()
        scene.background = new THREE.Color(0xfafafa)
        // scene.fog = new THREE.Fog(0xf0f0f0, 20, 40)
        this.#scene = scene
    }

    /** 初始化主容器 */
    _initMainGroup = () => {
        const group = new THREE.Group()

        // 劫持 add
        const originalAdd = group.add
        group.add = (...args) => {
            const result = originalAdd.apply(group, args)
            this._needRenderOnce = true
            return result
        }

        // 劫持 remove
        const originalRemove = group.remove
        group.remove = (...args) => {
            const result = originalRemove.apply(group, args)
            this._needRenderOnce = true
            return result
        }

        this.#scene.add(group)
        this._rootGroup = group
    }

    /** 初始化相机 */
    _initCamera = () => {
        const fov = 20
        const aspect = this.#rendererSize[0] / this.#rendererSize[1]
        const near = 0.1
        const far = 2000

        const camera = new THREE.PerspectiveCamera(fov, aspect, near, far)
        camera.position.set(...this.#cameraInitPos)
        camera.lookAt(0, 0, 0)

        this.#camera = camera
    }

    /** 初始化光线（可在子类中重写） */
    _initLight = () => {
        // 环境光（极弱）
        const ambient = new THREE.AmbientLight(0xffffff, 0.05)
        this.#scene.add(ambient)

        // 主光（关键光）
        const key = new THREE.DirectionalLight(0xffffff, 2.2)
        key.position.set(5, 10, 7)
        this.#scene.add(key)

        // 补光（弱）
        const fill = new THREE.DirectionalLight(0xffffff, 0.3)
        fill.position.set(-5, 3, 5)
        this.#scene.add(fill)

        // 轮廓光（增强边缘）
        const back = new THREE.DirectionalLight(0xffffff, 1.2)
        back.position.set(-5, 8, -8)
        this.#scene.add(back)

        RectAreaLightUniformsLib.init()
        // 👇 底部补光（核心）
        const bottomLight = new THREE.RectAreaLight(0xffffff, 0.5, 20, 20)

        // 放在模型下方
        bottomLight.position.set(0, -5, 0)

        // 朝上照
        bottomLight.rotation.x = Math.PI / 2

        this.#scene.add(bottomLight)
    }

    /** 初始化轨道控制器（子类可禁用或扩展） */
    _initOrbitController = () => {
        const orbit = new OrbitControls(this.#camera, this.#renderer.domElement)
        orbit.minPolarAngle = 0 // 限制相机最小仰角（0表示正上方）
        orbit.maxPolarAngle = Math.PI / 2 + 0.01 // 限制相机最大仰角，保持一个小的仰角
        orbit.enableDamping = true // 启用阻尼效果使相机移动更平滑
        orbit.dampingFactor = 0.05 // 设置阻尼系数
        orbit.enableZoom = true
        orbit.minDistance = 1
        orbit.maxDistance = 100
        orbit.enablePan = false

        this._orbit = orbit

        this._orbit.addEventListener('change', this._registerOrbitRenderRequest)
    }

    /** 注册轨道控制器渲染请求 */
    _registerOrbitRenderRequest = () => {
        this._requestRender('orbit')
    }

    /** 注销轨道控制器渲染请求 */
    _cancelOrbitRenderRequest = () => {
        this._cancelRender('orbit')
    }

    /** 开始渲染循环 */
    _startRenderLoop() {
        if (this._isRendering) return
        this._isRendering = true
        const loop = () => {
            if (!this._isRendering) return

            this._orbit?.update()

            this.#needRenderFrame && this._render()

            requestAnimationFrame(loop)
        }
        loop()
    }

    /** 停止渲染循环 */
    _stopRenderLoop() {
        this._isRendering = false
    }

    _render = () => {
        const delta = this.#clock.getDelta()

        for (const mixer of this.#animationMixers) {
            mixer.update(delta)
        }

        this._beforeRender(delta)

        this.#renderer?.render(this.#scene, this.#camera)

        this._afterRender(delta)

        this._needRenderOnce = false

        console.log('rendering...')

        if (!this._isOrbitDampingActive) {
            this._cancelOrbitRenderRequest()
        }
    }

    #orbitLastCameraPosition = new THREE.Vector3()
    #orbitLastTarget = new THREE.Vector3()
    /** orbit是否还在damping */
    get _isOrbitDampingActive() {
        const orbit = this._orbit
        if (!orbit) return false

        const EPS = 1e-5

        const position = orbit.object.position
        const target = orbit.target

        const moved =
            position.distanceToSquared(this.#orbitLastCameraPosition) > EPS ||
            target.distanceToSquared(this.#orbitLastTarget) > EPS

        // 更新缓存
        this.#orbitLastCameraPosition.copy(position)
        this.#orbitLastTarget.copy(target)

        return moved
    }

    /** 钩子：渲染前（可由子类覆写） */
    _beforeRender = () => { }

    /** 钩子：渲染后（可由子类覆写） */
    _afterRender = () => { }

    /** 当窗口大小变化时更新 */
    _resizeHandler = () => {
        if (!this.#renderer || !this.#camera) return

        // 更新渲染器尺寸
        this.#renderer.setSize(...this.#rendererSize)

        // 更新相机宽高比
        this.#camera.aspect = this.#rendererSize[0] / this.#rendererSize[1]
        this.#camera.updateProjectionMatrix()

        this._needRenderOnce = true
    }

    /** 释放单个对象的几何体与贴图 */
    _disposeObject3D = (object3D) => {
        if (!object3D) return
        object3D.traverse(child => {
            if (child.isMesh) {
                child.geometry?.dispose()
                const materials = Array.isArray(child.material)
                    ? child.material
                    : [child.material]
                for (const mat of materials) {
                    if (!mat) continue
                    for (const key of Object.keys(mat)) {
                        const value = mat[key]
                        if (value && value.isTexture) value.dispose()
                    }
                    mat.dispose()
                }
            }
        })
    }

    /** 移动相机到目标位置 */
    _moveCameraTo = (targetX, targetY, targetZ) => {
        const controls = this._orbit
        const camera = controls.object
        const target = controls.target

        // 禁用用户交互
        controls.enabled = false

        // 当前相机到目标点的向量
        const startOffset = camera.position.clone().sub(target)
        const startSpherical = new THREE.Spherical().setFromVector3(startOffset)

        // 目标位置对应的球坐标（相对于 target）
        const targetPos = new THREE.Vector3(targetX, targetY, targetZ)
        const targetOffset = targetPos.clone().sub(target)
        const targetSpherical = new THREE.Spherical().setFromVector3(targetOffset)

        const duration = 1000
        const startTime = performance.now()

        this._requestRender('camera')
        const animate = (now) => {
            const easeOutCubic = t => 1 - Math.pow(1 - t, 3)
            const t = easeOutCubic(Math.min((now - startTime) / duration, 1))

            // 球面插值
            const currentSpherical = new THREE.Spherical(
                THREE.MathUtils.lerp(startSpherical.radius, targetSpherical.radius, t),
                THREE.MathUtils.lerp(startSpherical.phi, targetSpherical.phi, t),
                THREE.MathUtils.lerp(startSpherical.theta, targetSpherical.theta, t)
            )

            const newPos = new THREE.Vector3().setFromSpherical(currentSpherical).add(target)
            camera.position.copy(newPos)
            camera.lookAt(target)
            controls.update()

            if (t < 1) {
                requestAnimationFrame(animate)
            } else {
                controls.enabled = true
                this._cancelRender('camera')
            }
        }

        requestAnimationFrame(animate)
    }

    /** 角度转为弧度 */
    _degToRad = val => THREE.MathUtils.degToRad(val)

    /** 加载 gltf 模型 */
    _loadGltf = (url) => {
        return new Promise((resolve, reject) => {
            this.#gltfLoader.load(url, resolve, undefined, reject)
        })
    }

    /** 获取3D对象的盒模型 */
    _getBox = obj => {
        const box = new THREE.Box3().setFromObject(obj)
        return box
    }

    /** 获取3D对象的中心点 */
    _getCenter = obj => {
        const box = this._getBox(obj)
        const center = new THREE.Vector3()
        box.getCenter(center)
        return center
    }

    /** 获取3D对象的尺寸 */
    _getSize = obj => {
        const box = this._getBox(obj)
        const size = new THREE.Vector3()
        box.getSize(size)
        return size
    }

    /** 开启坐标轴 */
    _showAxis = () => {
        const axisHelper = new THREE.AxesHelper(1000)
        this.#scene.add(axisHelper)
    }

    /*** 创建一个动画控制器，用于统一管理 glTF 中的 AnimationClip */
    _createAnimationController = (gltf) => {

        const { scene: model, animations } = gltf

        const mixer = new THREE.AnimationMixer(model)

        this.#animationMixers.push(mixer)

        const actions = {}

        animations.forEach((clip) => {
            actions[clip.name] = mixer.clipAction(clip)
        })

        const _getAction = (name) => {
            const action = actions[name]

            if (!action) {
                throw new Error(`动画不存在: ${name}`)
            }

            return action
        }

        const _getDuration = (action) => {
            return action.getClip().duration
        }

        const _waitActionFinish = (action) => {
            return new Promise(resolve => {
                const handler = (e) => {
                    if (e.action === action) {
                        mixer.removeEventListener('finished', handler)
                        resolve()
                    }

                }
                mixer.addEventListener('finished', handler)
            })
        }

        const forward = async (name = 'All Animations', options = {}) => {

            const action = _getAction(name)

            const { loop = THREE.LoopOnce, clamp = true } = options

            action.setLoop(loop)
            action.clampWhenFinished = clamp

            action.paused = false

            action.timeScale = Math.abs(action.timeScale || 1)

            this._requestRender(`mixer_forward_${model.uuid}`)

            action.play()

            await _waitActionFinish(action)

            this._cancelRender(`mixer_forward_${model.uuid}`)
            this._needRenderOnce = true
        }

        const reverse = async (name = 'All Animations', options = {}) => {

            const action = _getAction(name)

            const { loop = THREE.LoopOnce, clamp = true } = options

            const duration = _getDuration(action)

            /**
             * 如果当前在第一帧
             * 反向播放需要从最后一帧开始
             */
            if (action.time === 0) {
                action.time = duration
            }

            action.setLoop(loop)
            action.clampWhenFinished = clamp

            action.paused = false

            action.timeScale = -Math.abs(action.timeScale || 1)

            this._requestRender(`mixer_reverse_${model.uuid}`)

            action.play()

            await _waitActionFinish(action)

            this._cancelRender(`mixer_reverse_${model.uuid}`)
            this._needRenderOnce = true
        }

        const setProgress = (name = 'All Animations', progress) => {

            const action = _getAction(name)

            // 暂停动画
            action.paused = true

            // 必须 play 才能设置 time
            action.play()

            const duration = _getDuration(action)

            // 根据比例设置时间
            action.time = duration * progress
        }

        return {
            mixer,
            actions,
            forward,
            reverse,
            setProgress
        }
    }

    /** 清理指定 AnimationMixer */
    _clearAnimationMixer = (mixer) => {
        if (!mixer) return

        mixer.stopAllAction()

        const index = this.#animationMixers.indexOf(mixer)
        if (index !== -1) {
            this.#animationMixers.splice(index, 1)
        }

        mixer.uncacheRoot(mixer.getRoot())
    }

    /** 应用transform */
    _applyTransform = (gltf, transform) => {
        if (!gltf || !transform) return
        const directions = ['x', 'y', 'z']
        const { position, rotation } = transform

        if (rotation) {
            directions.forEach(direc => {
                const val = rotation[direc]
                if (val !== undefined) {
                    gltf.scene.rotation[direc] += this._degToRad(val)
                }
            })
        }

        if (position) {
            directions.forEach(direc => {
                const val = position[direc]
                if (val !== undefined) {
                    gltf.scene.position[direc] = val
                }
            })
        }

    }

    /** 应用材质 */
    _applyMaterial = (gltf, material) => {
        if (!gltf || !material) return

        gltf.scene.traverse(node => {
            if (!node.isMesh) return
            const config = material[node.material.name]
            if (config) {
                Object.keys(config).forEach(key => {
                    if (node.material[key]?.set) {
                        node.material[key].set(config[key])
                    } else {
                        node.material[key] = config[key]
                    }
                })
            }
        })

        this._needRenderOnce = true
    }
}